import { LabelType } from './label-types.enum';
import {
    locationIdentifiersKey,
    SlotDto,
    ZoneDto,
} from '@pf/platform-wms/domain';
import { ItemDto } from '@pf/platform-item-catalog/domain';
import { ZoneFragment } from '@control-tower/platform-wms';
import { IAddressDto } from '@pf/shared-common';
import { asArraySafe } from '@pf/shared-utility';

const QR_ALPHANUMERIC_CAPACITY_Q = [
    0, // unused
    13, // version 1 = 21 modules
    22, // version 2 = 25 modules
    36, // version 3 = 29 modules
    52, // version 4 = 33 modules
    72, // version 5 = 37 modules
    88, // version 6 = 41 modules
    102, // version 7 = 45 modules
    126, // version 8 = 49 modules
    154, // version 9 = 53 modules
    180, // version 10 = 57 modules
];

function assertQrAlphanumeric(value: string): void {
    const isValid = /^[0-9A-Z $%*+\-./:]+$/.test(value);
    if (!isValid) {
        throw new Error(
            `QR value contains characters outside QR alphanumeric mode: "${value}"`
        );
    }
}

function estimateQrModules(value: string): number {
    assertQrAlphanumeric(value.toUpperCase());
    const length = value.length;
    const version = QR_ALPHANUMERIC_CAPACITY_Q.findIndex(
        capacity => capacity >= length
    );
    if (version === -1) {
        throw new Error(
            `QR value is too long for local estimator table. Length: ${length}. Add QR versions above 10.`
        );
    }
    return 21 + (version - 1) * 4;
}

export interface PackingSlipLabelData {
    boxNumber: number;
    totalBoxes: number;
    trackingNumber?: string | null;
    orderNumber?: string | null;
    items: { description: string; quantity: number }[];
    shipTo?: IAddressDto | null;
    installAt?: IAddressDto | null;
    requestor?: string | null;
}

interface QrLayout {
    x: number;
    y: number;
    magnitude: number;
    textX: number;
    textWidth: number;
}

export type ItemForLabel = Pick<ItemDto, 'sku' | 'code' | 'name'>;

const calculateQrLayout = (barcode: string): QrLayout => {
    const labelWidth = 609;
    const labelHeight = 203;
    const leftMargin = 30;
    const padding = 10;
    const gap = 3;
    const minMagnitude = 4;
    const maxMagnitude = 7;

    const maxQrEdgeSize = labelHeight - padding * 3;

    const modules = estimateQrModules(barcode);
    const magnitude = Math.max(
        minMagnitude,
        Math.min(maxMagnitude, Math.floor(maxQrEdgeSize / modules))
    );

    const qrSize = modules * magnitude;
    const renderedSize = qrSize + 2;
    const x = leftMargin + padding;
    const y = Math.round((labelHeight - renderedSize) / 2);
    const textX = leftMargin + padding + maxQrEdgeSize + gap;
    const textWidth = labelWidth - textX - padding;

    return {
        x,
        y,
        magnitude,
        textX,
        textWidth,
    };
};

const fitFontSize = (
    text: string,
    textWidth: number,
    sizes: number[]
): number =>
    sizes.find(size => text.length * size * 0.6 <= textWidth) ??
    sizes[sizes.length - 1];

const LABEL_HEIGHT = 203;
const LABEL_V_PADDING = 10;
const LABEL_ROW_GAP = 8;
const LABEL_MIN_FONT = 12;

interface LabelRow {
    text: string;
    sizes: number[];
    maxLines?: number;
}

/**
 * Stacks the given text rows in the label's right-hand column, vertically
 * centered, and scales the whole block down if it would exceed the label
 * height. This guarantees the content is always middle-aligned and never
 * clipped, regardless of how long a name/location runs — so the small (609x203)
 * labels (Slot, Item, Handling Unit) don't need hand-tuned y-coordinates.
 * Returns only the text-block ZPL; the QR is rendered separately.
 */
const renderStackedRows = (qr: QrLayout, rows: LabelRow[]): string => {
    const visible = rows.filter(row => (row.text ?? '').trim().length > 0);
    if (!visible.length) {
        return '';
    }

    const heightOf = (m: { font: number; lines: number }) => m.lines * m.font;

    let measured = visible.map(row => {
        const maxLines = row.maxLines ?? 1;
        const font = fitFontSize(row.text, qr.textWidth * maxLines, row.sizes);
        const estLines = Math.ceil(
            (row.text.length * font * 0.6) / qr.textWidth
        );
        const lines = Math.min(maxLines, Math.max(1, estLines));
        return { text: row.text, font, lines };
    });

    const available = LABEL_HEIGHT - LABEL_V_PADDING * 2;
    let gap = LABEL_ROW_GAP;
    let contentHeight =
        measured.reduce((sum, m) => sum + heightOf(m), 0) +
        gap * (measured.length - 1);

    if (contentHeight > available) {
        const scale = available / contentHeight;
        gap = Math.max(4, Math.floor(gap * scale));
        measured = measured.map(m => ({
            ...m,
            font: Math.max(LABEL_MIN_FONT, Math.floor(m.font * scale)),
        }));
        contentHeight =
            measured.reduce((sum, m) => sum + heightOf(m), 0) +
            gap * (measured.length - 1);
    }

    let y = Math.max(
        LABEL_V_PADDING,
        Math.round((LABEL_HEIGHT - contentHeight) / 2)
    );

    return measured
        .map(m => {
            const block = `^CF0,${m.font}\n^FO${qr.textX},${y}^FB${qr.textWidth},${m.lines},0,L^FD${m.text}^FS`;
            y += heightOf(m) + gap;
            return block;
        })
        .join('\n\n');
};

export const LabelTemplates = {
    [LabelType.Zone]: (zone: ZoneDto | ZoneFragment) => {
        const zoneIdentifierText = locationIdentifiersKey(
            zone.locationIdentifiers
        );
        const barcodeText = zone.barcode ?? '';
        const qr = calculateQrLayout(barcodeText);
        const body = renderStackedRows(qr, [
            {
                text: `Zone ${zoneIdentifierText}`,
                sizes: [24, 22, 18],
                maxLines: 2,
            },
            { text: zone.zoneType ?? '', sizes: [22, 20, 18] },
            { text: barcodeText, sizes: [22, 18, 16], maxLines: 2 },
        ]);

        return `
^XA
^CI28
^PW609
^LL${LABEL_HEIGHT}

^FO${qr.x},${qr.y}^BQN,2,${qr.magnitude}
^FDQA,${barcodeText}^FS

${body}

^XZ`.trim();
    },
    [LabelType.Slot]: (slot: SlotDto) => {
        const equipmentTypeText = slot.equipmentType?.name
            ? `(${slot.equipmentType.name})`
            : '';
        // Human-readable location: Aisle / Bay / Level only. Business Unit and
        // Zone are levels 1-2, so drop them. The QR still encodes the full slot
        // barcode (the complete location identity).
        const locationText = asArraySafe(slot.zone?.locationIdentifiers)
            .slice()
            .sort((a, b) => (a.level ?? 0) - (b.level ?? 0))
            .filter(l => (l.level ?? 0) >= 3)
            .map(l => l.name)
            .join('-');
        const slotNumber = slot.barcode?.split('-')[1] ?? '';
        const locationLine = [
            locationText,
            slotNumber ? `Slot ${slotNumber}` : '',
        ]
            .filter(Boolean)
            .join('  |  ');
        const barcodeText = slot.barcode ?? '';
        const qr = calculateQrLayout(barcodeText);
        // Location is the hero of the label — size it up to fill the space and
        // stay legible at arm's length; the helper shrinks it only if needed.
        const body = renderStackedRows(qr, [
            {
                text: `Slot${equipmentTypeText ? ` ${equipmentTypeText}` : ''}`,
                sizes: [26, 24, 22],
            },
            { text: locationLine, sizes: [38, 32, 28, 24], maxLines: 2 },
            { text: slot.slotType ?? '', sizes: [26, 24, 22] },
        ]);

        return `
^XA
^CI28
^PW609
^LL${LABEL_HEIGHT}

^FO${qr.x},${qr.y}^BQN,2,${qr.magnitude}
^FDQA,${barcodeText}^FS

${body}

^XZ`.trim();
    },
    [LabelType.HandlingUnit]: (barcode: string, type: string) => {
        const qr = calculateQrLayout(barcode);
        const body = renderStackedRows(qr, [
            { text: 'Handling Unit', sizes: [30, 28, 24] },
            {
                text: `${type} ${barcode}`,
                sizes: [30, 24, 20, 16],
                maxLines: 2,
            },
        ]);
        return `
^XA
^CI28
^PW609
^LL${LABEL_HEIGHT}

^FO${qr.x},${qr.y}^BQN,2,${qr.magnitude}
^FDQA,${barcode}^FS

${body}

^XZ`.trim();
    },
    [LabelType.Item]: (item: ItemForLabel) => {
        const barcode = item.sku || item.code || '';
        const qr = calculateQrLayout(barcode);
        // Item name is the hero — allow two lines so long names stay large and
        // wrap rather than shrinking to fit a single line.
        const body = renderStackedRows(qr, [
            { text: 'Item', sizes: [24, 22, 20] },
            { text: item.name || '', sizes: [32, 28, 24, 20], maxLines: 2 },
            { text: barcode, sizes: [24, 20, 16] },
        ]);
        return `
^XA
^CI28
^PW609
^LL${LABEL_HEIGHT}

^FO${qr.x},${qr.y}^BQN,2,${qr.magnitude}
^FDQA,${barcode}^FS

${body}

^XZ`.trim();
    },
    [LabelType.PackingSlip]: (data: PackingSlipLabelData) => {
        const tracking = data.trackingNumber ?? '';
        const order = data.orderNumber ?? '';
        const sanitize = (s: string | null | undefined) =>
            (s ?? '').replace(/[\^~]/g, ' ');
        const formatAddressLines = (addr?: IAddressDto | null) => {
            if (!addr) return [];
            return [
                addr.name,
                addr.addressLine1,
                addr.addressLine2,
                [addr.city, addr.geoZone, addr.postalCode]
                    .filter(Boolean)
                    .join(' '),
            ]
                .filter(Boolean)
                .map(line => sanitize(line as string));
        };
        const shipToLines = formatAddressLines(data.shipTo);
        const installLines = formatAddressLines(data.installAt);
        const addressBlockY = 970;
        const addressLineHeight = 38;
        const renderAddressBlock = (lines: string[], xOrigin: number) =>
            lines
                .slice(0, 5)
                .map(
                    (line, idx) =>
                        `^FO${xOrigin},${addressBlockY + idx * addressLineHeight}^A0N,24,24^FD${line}^FS`
                )
                .join('\n');
        const shipToZpl = renderAddressBlock(shipToLines, 20);
        const installZpl = renderAddressBlock(installLines, 420);

        const itemRowHeight = 40;
        const itemsStartY = 320;
        const itemsEndY = 820;
        const maxItemsOnLabel = Math.floor(
            (itemsEndY - itemsStartY) / itemRowHeight
        );
        const visibleItems = data.items.slice(0, maxItemsOnLabel);
        const itemsZpl = visibleItems
            .map((item, idx) => {
                const y = itemsStartY + idx * itemRowHeight;
                const description = sanitize(item.description).slice(0, 60);
                const qty = String(item.quantity ?? 0);
                return `^FO20,${y}^A0N,24,24^FB620,1,0,L^FD${description}^FS
^FO670,${y}^A0N,24,24^FB120,1,0,R^FD${qty}^FS`;
            })
            .join('\n');
        const overflowZpl =
            data.items.length > maxItemsOnLabel
                ? `^FO20,${itemsStartY + maxItemsOnLabel * itemRowHeight}^A0N,22,22^FD+ ${data.items.length - maxItemsOnLabel} more item(s)^FS`
                : '';

        const qrCode = tracking ? `^FO610,20^BQN,2,7^FDQA,${tracking}^FS` : '';

        return `
^XA
^CI28
^PW812
^LL1218

^CF0,28
^FO20,20^FDBox ${data.boxNumber} of ${data.totalBoxes}^FS

^CF0,64
^FO20,60^FB570,1,0,L^FD${sanitize(tracking)}^FS

${qrCode}

^CF0,32
^FO20,160^FB770,1,0,L^FDOrder: ${sanitize(order)}^FS

^FO20,230^GB772,2,2^FS
^CF0,26
^FO30,240^FDITEM^FS
^FO670,240^FB120,1,0,R^FDQTY^FS
^FO20,280^GB772,2,2^FS

${itemsZpl}
${overflowZpl}

^FO20,830^GB772,2,2^FS

${data.requestor ? `^CF0,28\n^FO20,860^FB750,1,0,L^FD${sanitize(('ATTN: ' + data.requestor).toUpperCase())}^FS` : ''}

^CF0,30
^FO20,910^FDShip To:^FS
^FO420,910^FDInstall At:^FS

${shipToZpl}
${installZpl}

^XZ`.trim();
    },
};
